/**
 * Billing Dashboard — Light + Accent Stripe Design
 * Stat cards with colored top borders, avatar initials, plan badges, clickable rows
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CircularProgress } from '@mui/joy'
import { People, Bolt, WorkspacePremium, CheckCircle, Schedule, Search as SearchIcon, Visibility, Cached, Close } from '@mui/icons-material'
import { motion } from 'framer-motion'
import { isAxiosError } from 'axios'
import { SubscriptionPlan, type SubscriptionStatus } from '@numeo/types-core'
import { billingApi } from './api'
import { CustomerListItem, SubscriptionOverview } from './types'

const ease = [0.23, 1, 0.32, 1]

const STAT_CARDS = [
    { label: 'Total', key: 'total', color: 'border-t-slate-500', iconBg: 'bg-slate-100', iconColor: 'text-slate-600', Icon: People },
    { label: 'Free', key: 'free', color: 'border-t-gray-400', iconBg: 'bg-gray-100', iconColor: 'text-gray-500', Icon: People },
    { label: 'Pro', key: 'pro', color: 'border-t-blue-500', iconBg: 'bg-blue-50', iconColor: 'text-blue-600', Icon: Bolt },
    { label: 'Ultra', key: 'ultra', color: 'border-t-violet-500', iconBg: 'bg-violet-50', iconColor: 'text-violet-600', Icon: WorkspacePremium },
    { label: 'Active', key: 'active', color: 'border-t-emerald-500', iconBg: 'bg-emerald-50', iconColor: 'text-emerald-600', Icon: CheckCircle },
    { label: 'Trialing', key: 'trialing', color: 'border-t-amber-500', iconBg: 'bg-amber-50', iconColor: 'text-amber-600', Icon: Schedule },
]

function getInitials(email: string): string {
    if (!email || email === 'N/A') return '?'
    const parts = email.split('@')[0].split(/[._-]/)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return email.substring(0, 2).toUpperCase()
}

function getAvatarColor(email: string): string {
    const colors = [
        'bg-blue-100 text-blue-700',
        'bg-violet-100 text-violet-700',
        'bg-emerald-100 text-emerald-700',
        'bg-amber-100 text-amber-700',
        'bg-rose-100 text-rose-700',
        'bg-cyan-100 text-cyan-700',
        'bg-indigo-100 text-indigo-700',
    ]
    let hash = 0
    for (let i = 0; i < email.length; i++) hash = email.charCodeAt(i) + ((hash << 5) - hash)
    return colors[Math.abs(hash) % colors.length]
}

const planBadge = (plan: string) => {
    const styles: Record<string, string> = { PRO: 'bg-blue-100 text-blue-700', ULTRA: 'bg-violet-100 text-violet-700', FREE: 'bg-gray-100 text-gray-600' }
    return <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${styles[plan] || styles.FREE}`}>{plan}</span>
}

const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
        active: 'bg-emerald-50 text-emerald-700',
        trialing: 'bg-amber-50 text-amber-700',
        canceled: 'bg-red-50 text-red-700',
        past_due: 'bg-orange-50 text-orange-700',
    }
    return <span className={`text-[10px] px-2 py-0.5 rounded ${styles[status] || 'bg-gray-50 text-gray-600'}`}>{status}</span>
}

export default function BillingDashboard() {
    const navigate = useNavigate()
    const [overview, setOverview] = useState<SubscriptionOverview | null>(null)
    const [customers, setCustomers] = useState<CustomerListItem[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [totalPages, setTotalPages] = useState(0)
    const [loading, setLoading] = useState(false)
    const [overviewLoading, setOverviewLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [pageSize] = useState(20)
    const [searchEmail, setSearchEmail] = useState('')
    const [searchProject, setSearchProject] = useState('')
    const [filterPlan, setFilterPlan] = useState<SubscriptionPlan | ''>('')
    const [filterStatus, setFilterStatus] = useState<SubscriptionStatus | ''>('')
    const [cacheApiKey, setCacheApiKey] = useState('')
    const [cacheAppId, setCacheAppId] = useState('')
    const [cacheLoading, setCacheLoading] = useState(false)
    const [cacheResult, setCacheResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

    const fetchOverview = useCallback(async () => {
        try {
            setOverviewLoading(true)
            const data = await billingApi.getOverview()
            setOverview(data)
        } catch {
            /* ignore */
        } finally {
            setOverviewLoading(false)
        }
    }, [])

    const fetchCustomers = useCallback(
        async (p: number = page) => {
            try {
                setLoading(true)
                setError(null)
                const data = await billingApi.getCustomers({
                    email: searchEmail || undefined,
                    projectName: searchProject || undefined,
                    plan: filterPlan || undefined,
                    status: filterStatus || undefined,
                    page: p,
                    pageSize,
                })
                setCustomers(data.customers)
                setTotal(data.total)
                setPage(data.page)
                setTotalPages(data.totalPages)
            } catch (err: unknown) {
                setError((isAxiosError<{ message?: string }>(err) && err.response?.data?.message) || 'Failed to fetch customers')
            } finally {
                setLoading(false)
            }
        },
        [searchEmail, searchProject, filterPlan, filterStatus, page, pageSize]
    )

    useEffect(() => {
        fetchOverview()
    }, [fetchOverview])
    // Refetch only when a filter changes, not on every search keystroke: the effect calls the
    // latest fetchCustomers through a ref instead of depending on it.
    const fetchCustomersRef = useRef(fetchCustomers)
    useEffect(() => {
        fetchCustomersRef.current = fetchCustomers
    }, [fetchCustomers])
    useEffect(() => {
        fetchCustomersRef.current(1)
    }, [filterPlan, filterStatus])

    const handleSearch = () => fetchCustomers(1)
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSearch()
    }
    const hasActiveFilters = searchEmail || searchProject || filterPlan || filterStatus
    const clearFilters = () => {
        setSearchEmail('')
        setSearchProject('')
        setFilterPlan('')
        setFilterStatus('')
    }

    const handleClearCache = async () => {
        if (!cacheApiKey.trim()) return
        try {
            setCacheLoading(true)
            setCacheResult(null)
            const result = await billingApi.clearBillingCache(cacheApiKey.trim(), cacheAppId.trim() || undefined)
            setCacheResult({ type: 'success', message: result.message })
        } catch (err: unknown) {
            const response = isAxiosError<{ message?: string }>(err) ? err.response : undefined
            setCacheResult({
                type: 'error',
                message: response?.status === 403 ? 'Invalid API key' : response?.data?.message || 'Failed to clear cache',
            })
        } finally {
            setCacheLoading(false)
        }
    }

    const getStatValue = (key: string): number => {
        if (!overview) return 0
        if (key === 'total') return overview.total
        if (key === 'free') return overview.byPlan[SubscriptionPlan.FREE] || 0
        if (key === 'pro') return overview.byPlan[SubscriptionPlan.PRO] || 0
        if (key === 'ultra') return overview.byPlan[SubscriptionPlan.ULTRA] || 0
        if (key === 'active') return overview.byStatus['active'] || 0
        if (key === 'trialing') return overview.byStatus['trialing'] || 0
        return 0
    }

    return (
        <div className="p-6 w-full">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-5">
                <span>Home</span>
                <span>/</span>
                <span className="text-gray-600 font-medium">Billing</span>
            </div>

            {/* Page Header */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease }}
                className="flex items-center gap-3 mb-6"
            >
                <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
                    <People sx={{ fontSize: 18 }} className="text-indigo-600" />
                </div>
                <div>
                    <h1 className="text-xl font-bold text-gray-900">Billing Dashboard</h1>
                    <p className="text-sm text-gray-500">Customer billing overview and management</p>
                </div>
            </motion.div>

            {/* Stat Cards */}
            <div className="grid grid-cols-6 gap-3 mb-6">
                {STAT_CARDS.map((stat, i) => (
                    <motion.div
                        key={stat.label}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: i * 0.05, ease }}
                    >
                        <div
                            className={`bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200 border-t-[3px] ${stat.color} p-4`}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-[11px] font-semibold text-gray-400 tracking-wider uppercase">{stat.label}</p>
                                <div className={`w-7 h-7 rounded-md ${stat.iconBg} flex items-center justify-center`}>
                                    <stat.Icon sx={{ fontSize: 14 }} className={stat.iconColor} />
                                </div>
                            </div>
                            {overviewLoading ? <CircularProgress size="sm" /> : <p className="text-2xl font-bold text-gray-900">{getStatValue(stat.key)}</p>}
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Clear Cache */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.2, ease }}>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-6">
                    <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                        <div className="w-7 h-7 rounded-md bg-orange-50 flex items-center justify-center">
                            <Cached sx={{ fontSize: 14 }} className="text-orange-600" />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-gray-900">Clear Billing Cache</p>
                            <p className="text-xs text-gray-400">Redis entitlements + quota counters</p>
                        </div>
                    </div>
                    <div className="p-4">
                        {cacheResult && (
                            <div
                                className={`text-sm px-3 py-2 rounded-lg mb-3 ${cacheResult.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}
                            >
                                {cacheResult.message}
                            </div>
                        )}
                        <div className="flex gap-3 items-end">
                            <div className="flex-1">
                                <label className="text-xs font-medium text-gray-600 mb-1.5 block">Admin API Key</label>
                                <input
                                    type="password"
                                    placeholder="BILLING_ADMIN_API_KEY"
                                    value={cacheApiKey}
                                    onChange={(e) => setCacheApiKey(e.target.value)}
                                    className="w-full h-9 text-sm px-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300"
                                />
                            </div>
                            <div className="flex-1">
                                <label className="text-xs font-medium text-gray-600 mb-1.5 block">
                                    Application ID <span className="text-gray-400 font-normal">(optional)</span>
                                </label>
                                <input
                                    placeholder="Leave empty to clear all"
                                    value={cacheAppId}
                                    onChange={(e) => setCacheAppId(e.target.value)}
                                    className="w-full h-9 text-sm px-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300"
                                />
                            </div>
                            <button
                                onClick={handleClearCache}
                                disabled={!cacheApiKey.trim() || cacheLoading}
                                className="h-9 px-4 text-sm font-medium border border-gray-200 rounded-lg hover:bg-orange-50 hover:text-orange-700 hover:border-orange-200 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                            >
                                {cacheLoading ? <CircularProgress size="sm" sx={{ '--CircularProgress-size': '14px' }} /> : <Cached sx={{ fontSize: 14 }} />}
                                Clear
                            </button>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Customers Table */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.3, ease }}>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
                    {/* Header */}
                    <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                        <People sx={{ fontSize: 16 }} className="text-gray-500" />
                        <p className="text-sm font-semibold text-gray-900">Customers</p>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">{total}</span>
                    </div>

                    {/* Filters */}
                    <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50 flex gap-3 items-end">
                        <div className="flex-1">
                            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Search by Email</label>
                            <div className="relative">
                                <SearchIcon sx={{ fontSize: 14 }} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    placeholder="Search email..."
                                    value={searchEmail}
                                    onChange={(e) => setSearchEmail(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    className="w-full h-9 text-sm pl-8 pr-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300"
                                />
                            </div>
                        </div>
                        <div className="flex-1">
                            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Search by Project</label>
                            <div className="relative">
                                <SearchIcon sx={{ fontSize: 14 }} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    placeholder="Search project..."
                                    value={searchProject}
                                    onChange={(e) => setSearchProject(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    className="w-full h-9 text-sm pl-8 pr-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300"
                                />
                            </div>
                        </div>
                        <div className="w-32">
                            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Plan</label>
                            <select
                                value={filterPlan}
                                onChange={(e) => setFilterPlan(e.target.value as SubscriptionPlan | '')}
                                className="w-full h-9 text-sm px-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white"
                            >
                                <option value="">All Plans</option>
                                <option value="FREE">Free</option>
                                <option value="PRO">Pro</option>
                                <option value="ULTRA">Ultra</option>
                            </select>
                        </div>
                        <div className="w-32">
                            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Status</label>
                            <select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value as SubscriptionStatus | '')}
                                className="w-full h-9 text-sm px-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white"
                            >
                                <option value="">All Status</option>
                                <option value="active">Active</option>
                                <option value="trialing">Trialing</option>
                                <option value="canceled">Canceled</option>
                                <option value="past_due">Past Due</option>
                            </select>
                        </div>
                        <button
                            onClick={handleSearch}
                            disabled={loading}
                            className="h-9 px-4 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                        >
                            <SearchIcon sx={{ fontSize: 14 }} />
                            Search
                        </button>
                        {hasActiveFilters && (
                            <button onClick={clearFilters} className="h-9 px-3 text-xs text-gray-500 hover:text-red-600 flex items-center gap-1">
                                <Close sx={{ fontSize: 13 }} />
                                Clear
                            </button>
                        )}
                    </div>

                    {error && <div className="mx-5 mt-3 text-sm px-3 py-2 rounded-lg bg-red-50 text-red-700">{error}</div>}

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50/80 text-left">
                                    <th className="px-5 py-3 text-xs font-semibold text-gray-600 w-[52px]"></th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Project Name</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Owner</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Plan</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Status</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Seats</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600 text-right pr-5">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={7} className="text-center py-12">
                                            <CircularProgress size="sm" />
                                        </td>
                                    </tr>
                                ) : customers.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="text-center py-12 text-gray-400 text-sm">
                                            No customers found
                                        </td>
                                    </tr>
                                ) : (
                                    customers.map((c, idx) => (
                                        <motion.tr
                                            key={c.applicationId}
                                            initial={{ opacity: 0, x: -8 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.2, delay: idx * 0.03, ease }}
                                            className="hover:bg-indigo-50/40 cursor-pointer transition-colors duration-150 border-b border-gray-100 last:border-b-0 group"
                                            onClick={() => navigate(`/billing/${c.applicationId}`)}
                                        >
                                            <td className="py-2.5 pl-5 pr-0">
                                                <div
                                                    className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold ${getAvatarColor(c.ownerEmail)}`}
                                                >
                                                    {getInitials(c.ownerEmail)}
                                                </div>
                                            </td>
                                            <td className="py-2.5 px-2">
                                                <span className="font-medium text-gray-800 group-hover:text-indigo-700 transition-colors">{c.projectName}</span>
                                            </td>
                                            <td className="py-2.5 px-2 text-gray-600 truncate max-w-[200px]">{c.ownerEmail}</td>
                                            <td className="py-2.5 px-2">{planBadge(c.plan)}</td>
                                            <td className="py-2.5 px-2">{statusBadge(c.status)}</td>
                                            <td className="py-2.5 px-2 text-gray-600">
                                                {c.billableUsers}/{c.seats}
                                            </td>
                                            <td className="py-2.5 px-2 text-right pr-5" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    onClick={() => navigate(`/billing/${c.applicationId}`)}
                                                    className="w-7 h-7 rounded-md text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 opacity-0 group-hover:opacity-100 transition-all inline-flex items-center justify-center"
                                                >
                                                    <Visibility sx={{ fontSize: 16 }} />
                                                </button>
                                            </td>
                                        </motion.tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
                        <p className="text-xs text-gray-500">
                            {total > 0 ? `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}` : '0 results'}
                        </p>
                        <div className="flex items-center gap-2">
                            <button
                                disabled={page <= 1 || loading}
                                onClick={() => fetchCustomers(page - 1)}
                                className="px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                            >
                                Previous
                            </button>
                            <span className="text-xs text-gray-600">
                                Page {page} of {totalPages || 1}
                            </span>
                            <button
                                disabled={page >= totalPages || loading}
                                onClick={() => fetchCustomers(page + 1)}
                                className="px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    )
}
